// frontend/lib/hooks/useJobs.ts
"use client";

import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { apiGet, apiPost } from "../apiClient";

// =============================================================================
// Types
// =============================================================================

export type JobShiftType = "DAYS" | "NIGHTS" | "ROTATING";
export type JobStatus = "OPEN" | "PAUSED" | "FILLED" | "EXPIRED";

export interface JobItem {
  id: string;
  title: string;
  facility_name: string | null;
  specialty: string;
  city: string;
  state: string;
  shift_type: JobShiftType;
  shift_hours: number;
  contract_weeks: number;
  is_urgent: boolean;
  nurse_base_rate_hour: number | null;
  housing_stipend_week: number | null;
  // Extended fields from JobDetailResponse (when fetching single job)
  start_date?: string;
  bill_rate_hour?: number | null;
  mni_stipend_week?: number | null;
  status?: JobStatus;
}

export interface JobSearchResponse {
  results: JobItem[];
  page: number;
  page_size: number;
  total: number;
}

export interface JobSearchParams {
  q?: string;
  state?: string;
  specialty?: string;
  shift_type?: JobShiftType | "ANY";
  min_rate?: number;
  is_urgent?: boolean;
  page?: number;
  page_size?: number;
}

// =============================================================================
// Query Builder
// =============================================================================

function buildQuery(params: JobSearchParams): string {
  const sp = new URLSearchParams();
  if (params.q) sp.set("q", params.q);
  if (params.state) sp.set("state", params.state);
  if (params.specialty) sp.set("specialty", params.specialty);
  if (params.shift_type && params.shift_type !== "ANY") {
    sp.set("shift_type", params.shift_type);
  }
  if (typeof params.min_rate === "number") {
    sp.set("min_bill_rate", String(params.min_rate));
  }
  if (params.is_urgent) sp.set("is_urgent", "true");
  sp.set("page", String(params.page ?? 1));
  sp.set("page_size", String(params.page_size ?? 10));
  return sp.toString();
}

// =============================================================================
// Job Search Hook
// =============================================================================

export function useJobs(params: JobSearchParams) {
  const queryKey = ["jobs", params] as const;

  return useQuery<JobSearchResponse>({
    queryKey,
    queryFn: () => apiGet<JobSearchResponse>(`/jobs?${buildQuery(params)}`),
    placeholderData: keepPreviousData,
  });
}

// =============================================================================
// Apply to Job Mutation
// =============================================================================

export interface CreateApplicationResponse {
  id: string;
  job_id: string;
  status: string;
}

export function useApplyToJob() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      jobId,
      nurseNote,
    }: {
      jobId: string;
      nurseNote?: string;
    }) => {
      return apiPost<CreateApplicationResponse>("/applications", {
        job_id: jobId,
        nurse_note: nurseNote ?? "",
      });
    },
    onSuccess: () => {
      // Invalidate nurse applications so the "already applied" state updates
      qc.invalidateQueries({ queryKey: ["nurse-applications"] });
    },
  });
}
